import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Check, Sparkles, Users, Building2, Zap, Loader2 } from "lucide-react";
import { subscriptions } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import { toast } from "sonner";
import Header from "@/components/layout/Header";

const PLAN_ICONS = {
  free: Zap,
  plus: Sparkles,
  premium: Users,
  enterprise: Building2,
};

export default function Pricing() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(null);
  const [loadingPlans, setLoadingPlans] = useState(true);
  const [showEnterprise, setShowEnterprise] = useState(false);
  const [enterpriseForm, setEnterpriseForm] = useState({
    company_name: "",
    email: "",
    team_size: "",
    message: "",
  });

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      const response = await subscriptions.getPlans();
      setPlans(response.data);
    } catch {
      toast.error("Failed to load plans");
    }
    setLoadingPlans(false);
  };

  const handleSelectPlan = async (planId) => {
    if (planId === "free") {
      toast.info("You're already on the Free plan");
      return;
    }

    if (planId === "enterprise") {
      setShowEnterprise(true);
      return;
    }

    if (!user) {
      toast.error("Please sign in to upgrade");
      navigate("/auth");
      return;
    }

    setLoading(planId);
    try {
      const response = await subscriptions.createCheckout({
        plan_id: planId,
        origin_url: window.location.origin,
      });
      
      // Redirect to Stripe checkout
      window.location.href = response.data.url;
    } catch (error) {
      toast.error("Failed to start checkout. Please try again.");
      console.error(error);
    }
    setLoading(null);
  };

  const handleEnterpriseSubmit = async (e) => {
    e.preventDefault();
    try {
      await subscriptions.contactEnterprise(enterpriseForm);
      toast.success("Thank you! We'll contact you within 24 hours.");
      setShowEnterprise(false);
      setEnterpriseForm({ company_name: "", email: "", team_size: "", message: "" });
    } catch {
      toast.error("Failed to submit. Please try again.");
    }
  };

  const formatPrice = (plan) => {
    if (plan.price < 0) return "Custom";
    if (plan.price === 0) return "$0";
    return `$${plan.price}`;
  };

  const getPeriod = (plan) => {
    if (plan.price < 0) return "billed annually";
    if (plan.price === 0) return "forever";
    return "per user/month, billed annually";
  };

  const getCta = (plan) => {
    if (plan.id === "free") return "Current Plan";
    if (plan.id === "enterprise") return "Contact Sales";
    return `Upgrade to ${plan.name}`;
  };

  if (loadingPlans) {
    return (
      <div className="min-h-screen bg-white">
        <Header />
        <div className="flex items-center justify-center h-[60vh]">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <div className="max-w-7xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="text-center mb-16">
          <h1 className="text-4xl md:text-5xl font-bold text-slate-900 font-heading tracking-tight">
            Simple, transparent pricing
          </h1>
          <p className="mt-4 text-lg text-slate-600 max-w-2xl mx-auto font-body">
            Choose the plan that's right for you. All plans include a 14-day free trial.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {plans.map((plan) => {
            const Icon = PLAN_ICONS[plan.id] || Zap;
            const isHighlighted = plan.is_popular;
            return (
              <div
                key={plan.id}
                className={`relative rounded-2xl border ${
                  isHighlighted
                    ? "border-blue-500 shadow-lg shadow-blue-100"
                    : "border-slate-200"
                } p-6 flex flex-col`}
                data-testid={`plan-card-${plan.id}`}
              >
                {isHighlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                    <span className="bg-blue-500 text-white text-xs font-medium px-3 py-1 rounded-full">
                      Most Popular
                    </span>
                  </div>
                )}

                <div className="flex items-center gap-3 mb-4">
                  <div className={`p-2 rounded-lg ${isHighlighted ? "bg-blue-100" : "bg-slate-100"}`}>
                    <Icon className={`h-5 w-5 ${isHighlighted ? "text-blue-600" : "text-slate-600"}`} />
                  </div>
                  <h3 className="text-xl font-semibold text-slate-900">{plan.name}</h3>
                </div>

                <div className="mb-4">
                  <span className="text-4xl font-bold text-slate-900">{formatPrice(plan)}</span>
                  <span className="text-slate-500 text-sm ml-1">/{getPeriod(plan)}</span>
                </div>

                <p className="text-sm text-slate-600 mb-6 font-body">{plan.tagline}</p>

                <ul className="space-y-3 mb-8 flex-1">
                  {(plan.features || []).map((feature, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-slate-700">
                      <Check className="h-4 w-4 text-green-500 mt-0.5 shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                <Button
                  className={`w-full ${isHighlighted ? "" : "bg-slate-900 hover:bg-slate-800"}`}
                  variant={plan.id === "free" ? "outline" : "default"}
                  onClick={() => handleSelectPlan(plan.id)}
                  disabled={loading === plan.id || plan.id === "free"}
                  data-testid={`select-plan-${plan.id}`}
                >
                  {loading === plan.id ? "Loading..." : getCta(plan)}
                </Button>
              </div>
            );
          })}
        </div>

        {/* FAQ or Additional Info */}
        <div className="mt-20 text-center">
          <h2 className="text-2xl font-semibold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <div className="max-w-2xl mx-auto space-y-6 text-left">
            <div>
              <h3 className="font-medium text-slate-900">What are AI credits?</h3>
              <p className="text-sm text-slate-600 mt-1">
                AI credits are used when you generate diagrams from natural language or analyze source code with AI.
                Each generation uses 1 credit.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-slate-900">Can I cancel anytime?</h3>
              <p className="text-sm text-slate-600 mt-1">
                Yes, you can cancel your subscription at any time. You'll continue to have access until the end of your billing period.
              </p>
            </div>
            <div>
              <h3 className="font-medium text-slate-900">Do you offer refunds?</h3>
              <p className="text-sm text-slate-600 mt-1">
                We offer a 14-day money-back guarantee. If you're not satisfied, contact us for a full refund.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Enterprise Contact Dialog */}
      <Dialog open={showEnterprise} onOpenChange={setShowEnterprise}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Contact Sales</DialogTitle>
            <DialogDescription>
              Tell us about your organization and we'll get back to you within 24 hours.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleEnterpriseSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Company Name</label>
              <Input
                required
                value={enterpriseForm.company_name}
                onChange={(e) => setEnterpriseForm({ ...enterpriseForm, company_name: e.target.value })}
                placeholder="Acme Inc."
                data-testid="enterprise-company-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Work Email</label>
              <Input
                type="email"
                required
                value={enterpriseForm.email}
                onChange={(e) => setEnterpriseForm({ ...enterpriseForm, email: e.target.value })}
                placeholder="you@company.com"
                data-testid="enterprise-email-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Team Size</label>
              <Input
                required
                value={enterpriseForm.team_size}
                onChange={(e) => setEnterpriseForm({ ...enterpriseForm, team_size: e.target.value })}
                placeholder="50-100"
                data-testid="enterprise-size-input"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Message (optional)</label>
              <Textarea
                value={enterpriseForm.message}
                onChange={(e) => setEnterpriseForm({ ...enterpriseForm, message: e.target.value })}
                placeholder="Tell us about your needs..."
                rows={3}
                data-testid="enterprise-message-input"
              />
            </div>
            <div className="flex gap-2 justify-end">
              <Button type="button" variant="outline" onClick={() => setShowEnterprise(false)}>
                Cancel
              </Button>
              <Button type="submit" data-testid="enterprise-submit-btn">
                Submit
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
